import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { RefundMethod, RefundRequest, RefundType } from '../../../../../../domain/models/refund.model';

export interface RefundDialogData {
  /** 'request' collects a full RefundRequest; 'reject' only collects a reason. */
  mode: 'request' | 'reject';
  receiptNumber: string;
  paymentAmount: number;
  /** Amount not yet refunded (request mode). */
  refundable: number;
}

/** Request a refund on a payment, or give the reason for rejecting one. */
@Component({
  selector: 'app-refund-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule,
    MatInputModule, MatSelectModule, MatIconModule],
  template: `
    <h2 mat-dialog-title class="dlg-title">
      <span class="dlg-icon"><mat-icon>{{ data.mode === 'request' ? 'undo' : 'block' }}</mat-icon></span>
      {{ data.mode === 'request' ? 'Request a refund' : 'Reject refund' }}
    </h2>
    <mat-dialog-content class="dlg-body">
      <p class="hint">
        Receipt <strong>{{ data.receiptNumber }}</strong> · paid {{ data.paymentAmount | currency }}
        <ng-container *ngIf="data.mode === 'request'"> · refundable {{ data.refundable | currency }}</ng-container>
      </p>

      <ng-container *ngIf="data.mode === 'request'">
        <div class="row">
          <mat-form-field appearance="outline">
            <mat-label>Type</mat-label>
            <mat-select [(ngModel)]="refundType" (ngModelChange)="onType()">
              <mat-option value="FULL">Full</mat-option>
              <mat-option value="PARTIAL">Partial</mat-option>
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Amount</mat-label>
            <span matTextPrefix>$&nbsp;</span>
            <input matInput type="number" min="0.01" [max]="data.refundable" step="0.01"
                   [(ngModel)]="amount" [disabled]="refundType === 'FULL'">
            <mat-error>Enter an amount up to {{ data.refundable | currency }}</mat-error>
          </mat-form-field>
        </div>
        <div class="row">
          <mat-form-field appearance="outline">
            <mat-label>Refund by</mat-label>
            <mat-select [(ngModel)]="method">
              <mat-option value="ORIGINAL_METHOD">Original payment method</mat-option>
              <mat-option value="CHECK">Check</mat-option>
              <mat-option value="ACH">ACH</mat-option>
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline" *ngIf="method === 'CHECK'">
            <mat-label>Check number</mat-label>
            <input matInput [(ngModel)]="checkNumber" maxlength="50">
          </mat-form-field>
        </div>
      </ng-container>

      <mat-form-field appearance="outline" class="full">
        <mat-label>Reason</mat-label>
        <input matInput [(ngModel)]="reason" maxlength="500" required
               placeholder="e.g. duplicate payment, overpayment, returned check">
      </mat-form-field>
      <mat-form-field appearance="outline" class="full" *ngIf="data.mode === 'request'">
        <mat-label>Notes</mat-label>
        <textarea matInput [(ngModel)]="notes" maxlength="2000" rows="2"></textarea>
      </mat-form-field>
    </mat-dialog-content>
    <mat-dialog-actions align="end" class="dlg-actions">
      <button mat-stroked-button (click)="ref.close()">Cancel</button>
      <button mat-flat-button [color]="data.mode === 'request' ? 'primary' : 'warn'" [disabled]="!valid()" (click)="confirm()">
        <mat-icon>{{ data.mode === 'request' ? 'send' : 'block' }}</mat-icon>
        {{ data.mode === 'request' ? 'Request refund' : 'Reject' }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .dlg-title { display:flex; align-items:center; gap:12px; font-size:20px; font-weight:600; color:#041E49; }
    .dlg-icon { display:inline-flex; align-items:center; justify-content:center; width:40px; height:40px;
      border-radius:10px; background:#E3F2FD; color:#1565C0; }
    .dlg-body { padding-top:8px; min-width:min(560px,86vw); }
    .hint { color:rgba(0,0,0,.6); font-size:13px; margin:0 0 16px; }
    .row { display:flex; gap:12px; } .row mat-form-field { flex:1; }
    .full { width:100%; }
    .dlg-actions { padding:8px 24px 16px; gap:8px; }
  `]
})
export class RefundDialogComponent {
  refundType: RefundType = 'FULL';
  amount = this.data.refundable;
  method: RefundMethod = 'ORIGINAL_METHOD';
  checkNumber = '';
  reason = '';
  notes = '';

  constructor(public ref: MatDialogRef<RefundDialogComponent, RefundRequest | string>,
              @Inject(MAT_DIALOG_DATA) public data: RefundDialogData) {}

  onType(): void {
    if (this.refundType === 'FULL') this.amount = this.data.refundable;
  }

  valid(): boolean {
    if (!this.reason.trim()) return false;
    if (this.data.mode === 'reject') return true;
    return this.amount > 0 && this.amount <= this.data.refundable + 0.005
      && (this.method !== 'CHECK' || !!this.checkNumber.trim());
  }

  confirm(): void {
    if (this.data.mode === 'reject') { this.ref.close(this.reason.trim()); return; }
    this.ref.close({
      refundType: this.refundType,
      refundAmount: Number(this.amount.toFixed(2)),
      reason: this.reason.trim(),
      notes: this.notes.trim() || undefined,
      refundMethod: this.method,
      checkNumber: this.method === 'CHECK' ? this.checkNumber.trim() : undefined,
    });
  }
}
